import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import { Plus, FileText, Lock, CheckCircle2, Clock, Loader2, Trash2, Copy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/plans");
      setPlans(data);
    } catch {
      setPlans([]);
    }
  };
  useEffect(() => { load(); }, []);

  const createNew = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/plans", {
        title: "Min forretningsplan",
        inputs: {},
      });
      navigate(`/plan/${data.plan_id}/wizard`);
    } catch {
      toast.error("Kunne ikke opprette plan");
    } finally {
      setCreating(false);
    }
  };

  const removePlan = async (id) => {
    try {
      await api.delete(`/plans/${id}`);
      toast.success("Plan slettet");
      load();
    } catch {
      toast.error("Kunne ikke slette plan");
    }
  };

  const duplicatePlan = async (id) => {
    try {
      const { data } = await api.post(`/plans/${id}/duplicate`);
      toast.success("Plan duplisert");
      load();
      return data;
    } catch {
      toast.error("Kunne ikke duplisere plan");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Dashbord</p>
            <h1 className="mt-2 text-4xl font-medium tracking-tight text-slate-900" data-testid="dashboard-title">
              Hei, {user?.name?.split(" ")[0] || "der"}.
            </h1>
            <p className="mt-2 text-slate-600">Her finner du alle dine forretningsplaner.</p>
          </div>
          <Button onClick={createNew} disabled={creating} className="rounded-full bg-slate-900 hover:bg-slate-800 h-11 px-6" data-testid="new-plan-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> Ny forretningsplan</>}
          </Button>
        </div>

        {plans && plans.length > 0 && (
          <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6" data-testid="dashboard-stats">
            <Card className="bg-white border border-gray-200 rounded-2xl">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Totalt</p>
                <p className="mt-2 text-3xl font-medium tracking-tight text-slate-900" data-testid="stat-total">{plans.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-gray-200 rounded-2xl">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Generert</p>
                <p className="mt-2 text-3xl font-medium tracking-tight text-slate-900" data-testid="stat-generated">{plans.filter(p => p.status === "generated").length}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-gray-200 rounded-2xl">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Låst opp</p>
                <p className="mt-2 text-3xl font-medium tracking-tight text-emerald-700" data-testid="stat-unlocked">{plans.filter(p => p.unlocked).length}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="mt-12">
          {plans === null ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : plans.length === 0 ? (
            <Card className="bg-white border-dashed border-2 border-gray-200 rounded-2xl">
              <CardContent className="p-16 text-center">
                <div className="mx-auto h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-slate-500" />
                </div>
                <h3 className="text-xl font-medium tracking-tight text-slate-900">Ingen planer enda</h3>
                <p className="mt-2 text-slate-500 max-w-sm mx-auto">Lag din første forretningsplan på under 10 minutter.</p>
                <Button onClick={createNew} disabled={creating} className="mt-6 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="empty-create-btn">
                  <Plus className="h-4 w-4 mr-2" /> Lag forretningsplan
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((p) => (
                <Card key={p.plan_id} className="bg-white border border-gray-200 rounded-2xl hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-testid={`plan-card-${p.plan_id}`}>
                  <CardContent className="p-7 flex flex-col h-full">
                    <div className="flex items-start justify-between gap-3">
                      <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-slate-700" />
                      </div>
                      {p.unlocked ? (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" /> Låst opp</Badge>
                      ) : p.status === "generating" ? (
                        <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Genererer</Badge>
                      ) : p.status === "generated" ? (
                        <Badge variant="secondary"><Lock className="h-3 w-3 mr-1" /> Forhåndsvisning</Badge>
                      ) : p.status === "error" ? (
                        <Badge variant="destructive">Feil</Badge>
                      ) : (
                        <Badge variant="outline"><Clock className="h-3 w-3 mr-1" /> Utkast</Badge>
                      )}
                    </div>
                    <h3 className="mt-5 text-lg font-medium tracking-tight text-slate-900 line-clamp-1">
                      {p.inputs?.business_name || p.title || "Min forretningsplan"}
                    </h3>
                    <p className="mt-1 text-sm text-slate-500 line-clamp-2 min-h-[40px]">
                      {p.inputs?.business_idea || "Ingen beskrivelse enda."}
                    </p>
                    <p className="mt-3 text-xs text-slate-400">
                      Sist oppdatert {new Date(p.updated_at || p.created_at).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                      <Button asChild variant="outline" className="rounded-full flex-1" data-testid={`open-plan-${p.plan_id}`}>
                        <Link to={p.status === "generated" ? `/plan/${p.plan_id}` : `/plan/${p.plan_id}/wizard`}>
                          {p.status === "generated" ? "Åpne" : "Fortsett"}
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => duplicatePlan(p.plan_id)}
                        className="rounded-full text-slate-400 hover:text-slate-900"
                        title="Dupliser plan"
                        data-testid={`duplicate-plan-${p.plan_id}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full text-slate-400 hover:text-red-600" data-testid={`delete-plan-${p.plan_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Slette denne planen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Dette kan ikke angres. Planen og alt innhold vil bli slettet permanent.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Avbryt</AlertDialogCancel>
                            <AlertDialogAction onClick={() => removePlan(p.plan_id)} className="bg-red-600 hover:bg-red-700">
                              Slett
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
